import React, { useState, useEffect, useRef } from 'react';
import API_BASE_URL from '../config/apiConfig';

const ResetPasswordForm = ({ isVisible, onBackToLogin }) => {
  // State quản lý các bước
  const [step, setStep] = useState(1); // 1: Enter email, 2: Enter OTP, 3: Reset password
  
  // State cho bước 1: Nhập email
  const [email, setEmail] = useState('');
  const [isLoadingEmail, setIsLoadingEmail] = useState(false);
  const [emailError, setEmailError] = useState('');
  const [emailSuccess, setEmailSuccess] = useState('');
  const [cooldownSeconds, setCooldownSeconds] = useState(0);
  
  // State cho bước 2: Nhập OTP
  const [otp, setOtp] = useState('');
  const [isLoadingOtp, setIsLoadingOtp] = useState(false);
  const [otpError, setOtpError] = useState('');
  const [otpSuccess, setOtpSuccess] = useState('');
  
  // State cho bước 3: Đặt lại mật khẩu
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoadingReset, setIsLoadingReset] = useState(false);
  const [resetError, setResetError] = useState('');
  const [resetSuccess, setResetSuccess] = useState(false);
  
  // Trạng thái các điều kiện mật khẩu
  const [validations, setValidations] = useState({
    length: false,
    numberOrSymbol: false,
    upperLowerCase: false
  });

  // Function để reset tất cả state
  const resetAllState = () => {
    setStep(1);
    setEmail('');
    setOtp('');
    setPassword('');
    setConfirmPassword('');
    setIsLoadingEmail(false);
    setIsLoadingOtp(false);
    setIsLoadingReset(false);
    setEmailError('');
    setEmailSuccess('');
    setOtpError('');
    setOtpSuccess('');
    setResetError('');
    setResetSuccess(false);
    setCooldownSeconds(0);
    setValidations({
      length: false,
      numberOrSymbol: false,
      upperLowerCase: false
    });
  };

  // Track previous isVisible value
  const prevIsVisibleRef = useRef(isVisible);

  // Reset tất cả state khi component được hiển thị
  useEffect(() => {
    if (isVisible) {
      resetAllState();
    }
  }, [isVisible]);

  // Reset khi component bị ẩn (khi quay về login)
  useEffect(() => {
    // If component was visible and now is not, reset state
    if (prevIsVisibleRef.current && !isVisible) {
      resetAllState();
    }
    prevIsVisibleRef.current = isVisible;
  }, [isVisible]);

  // Countdown timer effect
  useEffect(() => {
    let interval = null;
    if (cooldownSeconds > 0) {
      interval = setInterval(() => {
        setCooldownSeconds((prev) => {
          if (prev <= 1) {
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    } else if (cooldownSeconds === 0 && interval) {
      clearInterval(interval);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [cooldownSeconds]);

  // Hàm kiểm tra mật khẩu mỗi khi user gõ
  useEffect(() => {
    setValidations({
      length: password.length >= 8,
      numberOrSymbol: /[0-9!@#$%^&*(),.?":{}|<>]/.test(password),
      upperLowerCase: /[a-z]/.test(password) && /[A-Z]/.test(password)
    });
  }, [password]);

  // Tính % độ mạnh để hiển thị thanh màu vàng/xanh
  const strengthPercentage = Object.values(validations).filter(Boolean).length * 33.33;

  // Bước 1: Gửi OTP qua email
  const handleSendOtp = async (e) => {
    e.preventDefault();
    setEmailError('');
    setEmailSuccess('');
    setIsLoadingEmail(true);

    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/forgot-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      });

      const data = await response.json();

      // Always check for cooldown first, regardless of success status
      if (data.remainingSeconds !== undefined && data.remainingSeconds > 0) {
        setCooldownSeconds(data.remainingSeconds);
        setEmailError(data.message || 'Please wait before requesting a new OTP');
        setEmailSuccess('');
      } else if (data.success) {
        setEmailSuccess('OTP code has been sent to your email');
        setEmailError('');
        setStep(2); // Move to OTP step
        setCooldownSeconds(0); // Reset cooldown on success
      } else {
        // If no cooldown info but still failed, show error
        setEmailError(data.message || 'An error occurred');
        setEmailSuccess('');
      }
    } catch (error) {
      console.error('Send OTP error:', error);
      setEmailError('Connection error. Please try again.');
    } finally {
      setIsLoadingEmail(false);
    }
  };

  // Bước 2: Xác thực OTP
  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    setOtpError('');
    setOtpSuccess('');
    setIsLoadingOtp(true);

    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/verify-otp`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, otp }),
      });

      const data = await response.json();

      if (data.success) {
        setOtpSuccess('OTP verified successfully');
        setStep(3); // Move to reset password step
      } else {
        setOtpError(data.message || 'Invalid OTP code');
      }
    } catch (error) {
      console.error('Verify OTP error:', error);
      setOtpError('Connection error. Please try again.');
    } finally {
      setIsLoadingOtp(false);
    }
  };

  // Bước 3: Đặt lại mật khẩu
  const handleResetPassword = async (e) => {
    e.preventDefault();
    setResetError('');

    // Check if passwords match
    if (password !== confirmPassword) {
      setResetError('Passwords do not match');
      return;
    }

    // Check password strength
    if (!validations.length || !validations.numberOrSymbol || !validations.upperLowerCase) {
      setResetError('Password does not meet requirements');
      return;
    }

    setIsLoadingReset(true);

    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/reset-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, otp, newPassword: password }),
      });

      const data = await response.json();

      if (data.success) {
        setResetSuccess(true);
        // After 2 seconds redirect to login page
        setTimeout(() => {
          resetAllState(); // Reset all state before going back to login
          if (onBackToLogin) onBackToLogin();
        }, 2000);
      } else {
        setResetError(data.message || 'Failed to reset password');
      }
    } catch (error) {
      console.error('Reset password error:', error);
      setResetError('Connection error. Please try again.');
    } finally {
      setIsLoadingReset(false);
    }
  };

  // Gửi lại OTP
  const handleResendOtp = async () => {
    setOtpError('');
    setOtpSuccess('');
    setIsLoadingOtp(true);

    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/forgot-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      });

      const data = await response.json();

      // Always check for cooldown first, regardless of success status
      if (data.remainingSeconds !== undefined && data.remainingSeconds > 0) {
        setCooldownSeconds(data.remainingSeconds);
        setOtpError(data.message || 'Please wait before requesting a new OTP');
        setOtpSuccess('');
      } else if (data.success) {
        setOtpSuccess('New OTP code has been sent to your email');
        setOtpError('');
        setCooldownSeconds(0); // Reset cooldown on success
      } else {
        // If no cooldown info but still failed, show error
        setOtpError(data.message || 'Failed to resend OTP code');
        setOtpSuccess('');
      }
    } catch (error) {
      console.error('Resend OTP error:', error);
      setOtpError('Connection error. Please try again.');
    } finally {
      setIsLoadingOtp(false);
    }
  };

  if (!isVisible) return null;

  return (
    <div className={`login-container ${isVisible ? 'visible' : 'hidden'}`}>
      <div className="form-box">
        <div className="header-text">
          <h2>Reset password</h2>
          <p>
            {step === 1 && 'Enter your email to receive OTP code'}
            {step === 2 && 'Enter the OTP code sent to your email'}
            {step === 3 && 'Set your new password'}
          </p>
        </div>

        {/* Bước 1: Nhập email */}
        {step === 1 && (
          <form onSubmit={handleSendOtp}>
            {emailError && (
              <div style={{
                padding: '12px',
                marginBottom: '16px',
                background: '#fee2e2',
                color: '#dc2626',
                borderRadius: '8px',
                fontSize: '14px'
              }}>
                {emailError}
              </div>
            )}
            {emailSuccess && (
              <div style={{
                padding: '12px',
                marginBottom: '16px',
                background: '#d1fae5',
                color: '#065f46',
                borderRadius: '8px',
                fontSize: '14px'
              }}>
                {emailSuccess}
              </div>
            )}

            <div className="input-group">
              <input 
                type="email" 
                placeholder="joe.doe@gmail.com" 
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                disabled={isLoadingEmail}
              />
            </div>

            <button 
              type="submit" 
              className="login-btn" 
              disabled={isLoadingEmail || cooldownSeconds > 0}
            >
              {isLoadingEmail 
                ? 'Sending...' 
                : cooldownSeconds > 0 
                  ? `Please wait ${cooldownSeconds}s` 
                  : 'Send OTP Code'}
            </button>
            
            {cooldownSeconds > 0 && (
              <div style={{
                textAlign: 'center',
                marginTop: '10px',
                fontSize: '14px',
                color: '#6b7280'
              }}>
                You can request a new OTP in {cooldownSeconds} seconds
              </div>
            )}

            <div style={{textAlign: 'center', marginTop: '15px'}}>
              <a href="#" onClick={(e) => {
                e.preventDefault();
                resetAllState(); // Reset all state before going back to login
                if (onBackToLogin) onBackToLogin();
              }} style={{color: '#6b7280', textDecoration: 'none', fontSize: '0.9rem'}}>
                Back to Login
              </a>
            </div>
          </form>
        )}

        {/* Bước 2: Nhập OTP */}
        {step === 2 && (
          <form onSubmit={handleVerifyOtp}>
            {otpError && (
              <div style={{
                padding: '12px',
                marginBottom: '16px',
                background: '#fee2e2',
                color: '#dc2626',
                borderRadius: '8px',
                fontSize: '14px'
              }}>
                {otpError}
              </div>
            )}
            {otpSuccess && (
              <div style={{
                padding: '12px',
                marginBottom: '16px',
                background: '#d1fae5',
                color: '#065f46',
                borderRadius: '8px',
                fontSize: '14px'
              }}>
                {otpSuccess}
              </div>
            )}

            <div className="input-group">
              <input 
                type="text" 
                placeholder="Enter OTP code (6 digits)" 
                value={otp}
                onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                required
                disabled={isLoadingOtp}
                maxLength={6}
                style={{textAlign: 'center', fontSize: '20px', letterSpacing: '8px'}}
              />
            </div>

            <button type="submit" className="login-btn" disabled={isLoadingOtp}>
              {isLoadingOtp ? 'Verifying...' : 'Verify OTP'}
            </button>

            <div style={{textAlign: 'center', marginTop: '15px'}}>
              <a 
                href="#" 
                onClick={(e) => {
                  e.preventDefault();
                  if (cooldownSeconds === 0) {
                    handleResendOtp();
                  }
                }} 
                style={{
                  color: cooldownSeconds > 0 ? '#9ca3af' : '#6b7280', 
                  textDecoration: 'none', 
                  fontSize: '0.9rem',
                  cursor: cooldownSeconds > 0 ? 'not-allowed' : 'pointer',
                  pointerEvents: cooldownSeconds > 0 ? 'none' : 'auto'
                }}
              >
                {cooldownSeconds > 0 
                  ? `Resend OTP Code (${cooldownSeconds}s)` 
                  : 'Resend OTP Code'}
              </a>
            </div>
            
            {cooldownSeconds > 0 && (
              <div style={{
                textAlign: 'center',
                marginTop: '5px',
                fontSize: '12px',
                color: '#9ca3af'
              }}>
                Please wait {cooldownSeconds} seconds before requesting a new code
              </div>
            )}

            <div style={{textAlign: 'center', marginTop: '10px'}}>
              <a href="#" onClick={(e) => {
                e.preventDefault();
                setStep(1);
                setOtp('');
                setOtpError('');
                setOtpSuccess('');
              }} style={{color: '#6b7280', textDecoration: 'none', fontSize: '0.9rem'}}>
                Back
              </a>
            </div>
          </form>
        )}

        {/* Bước 3: Đặt lại mật khẩu */}
        {step === 3 && (
          <form onSubmit={handleResetPassword}>
            {resetError && (
              <div style={{
                padding: '12px',
                marginBottom: '16px',
                background: '#fee2e2',
                color: '#dc2626',
                borderRadius: '8px',
                fontSize: '14px'
              }}>
                {resetError}
              </div>
            )}
            {resetSuccess && (
              <div style={{
                padding: '12px',
                marginBottom: '16px',
                background: '#d1fae5',
                color: '#065f46',
                borderRadius: '8px',
                fontSize: '14px'
              }}>
                Password reset successfully! Redirecting to login page...
              </div>
            )}

            {/* Mật khẩu mới */}
            <div className="input-group password-group">
              <input 
                type="password" 
                placeholder="............" 
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={isLoadingReset || resetSuccess}
              />
              <span className="eye-icon">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path><circle cx="12" cy="12" r="3"></circle></svg>
              </span>
            </div>

            {/* Thanh độ mạnh mật khẩu */}
            <div className="strength-meter">
              <div 
                className="strength-fill" 
                style={{ 
                  width: `${strengthPercentage}%`,
                  backgroundColor: strengthPercentage === 100 ? '#10b981' : '#f59e0b'
                }}
              ></div>
            </div>

            {/* Danh sách điều kiện */}
            <ul className="validation-list">
              <li className={validations.length ? 'valid' : ''}>
                {validations.length ? '✓' : '•'} Least 8 characters
              </li>
              <li className={validations.numberOrSymbol ? 'valid' : ''}>
                {validations.numberOrSymbol ? '✓' : '•'} Least one number (0-9) or symbol
              </li>
              <li className={validations.upperLowerCase ? 'valid' : ''}>
                {validations.upperLowerCase ? '✓' : '•'} Lowercase (a-z) and uppercase (A-Z)
              </li>
            </ul>

            {/* Nhập lại mật khẩu */}
            <div className="input-group password-group" style={{marginTop: '20px'}}>
              <input 
                type="password" 
                placeholder="Confirm new password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                disabled={isLoadingReset || resetSuccess}
              />
              <span className="eye-icon">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path><circle cx="12" cy="12" r="3"></circle></svg>
              </span>
            </div>

            {/* Nút Submit */}
            <button 
              type="submit" 
              className="login-btn" 
              style={{marginTop: '30px'}}
              disabled={isLoadingReset || resetSuccess}
            >
              {isLoadingReset ? 'Processing...' : resetSuccess ? 'Success!' : 'Reset Password'}
            </button>

            <div style={{textAlign: 'center', marginTop: '15px'}}>
              <a href="#" onClick={(e) => {
                e.preventDefault();
                setStep(2);
                setPassword('');
                setConfirmPassword('');
                setResetError('');
              }} style={{color: '#6b7280', textDecoration: 'none', fontSize: '0.9rem'}}>
                Back
              </a>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default ResetPasswordForm;
